import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useNotificationStore } from '../store/notificationStore';
import { useInstagramStore } from '../store/instagramStore';
import { useAuthStore } from '../store/authStore';

export const useNotificationManager = () => {
  const { user } = useAuthStore();
  const {
    addNotification,
    notifyPostDue,
    notifyShootingReminder,
    cleanExpiredNotifications
  } = useNotificationStore(useShallow((state: any) => ({
    addNotification: state.addNotification,
    notifyPostDue: state.notifyPostDue,
    notifyShootingReminder: state.notifyShootingReminder,
    cleanExpiredNotifications: state.cleanExpiredNotifications
  })));

  const { contracts, posts } = useInstagramStore(useShallow(state => ({
    contracts: state.contracts,
    posts: state.posts
  })));

  // Fonction pour créer des notifications de démo (désactivée par défaut)
  const createSampleNotifications = () => {
    if (!user) return;

    // Les notifications de démo avec des ID stables pour éviter les doublons
    const demoNotifications = [
      {
        type: 'POST_TO_SCHEDULE' as const,
        title: 'Post à programmer',
        message: 'Le post "Plat du jour" doit être programmé pour demain',
        relatedId: 'demo_post_1',
        relatedType: 'instagram_post' as const
      },
      {
        type: 'NEW_CLIENT' as const,
        title: 'Nouveau client ajouté',
        message: 'Le client "Pizzeria Mario" a été ajouté et nécessite votre attention',
        relatedId: 'demo_client_new',
        relatedType: 'client' as const
      },
      {
        type: 'SHOOTING_REMINDER' as const,
        title: 'Rappel shooting',
        message: 'Shooting programmé pour Restaurant Le Gourmet demain à 14h',
        relatedId: 'demo_contract_1',
        relatedType: 'instagram_contract' as const
      }
    ];

    demoNotifications.forEach(notification => {
      addNotification(notification);
    });
  };

  // Vérifier les posts en retard
  const checkOverduePosts = () => {
    const now = new Date();
    posts.forEach(post => {
      if (post.scheduledDate && post.status !== 'published') {
        const scheduledDate = new Date(post.scheduledDate);
        if (scheduledDate < now) {
          notifyPostDue(post.name, post.id);
        }
      }
    });
  };

  // Vérifier les shootings à venir
  const checkUpcomingShootings = () => {
    const now = new Date();
    const tomorrow = new Date(now.getTime() + (24 * 60 * 60 * 1000));
    
    contracts.forEach(contract => {
      if (contract.shootingDate) {
        const shootingDate = new Date(contract.shootingDate);
        if (shootingDate.toDateString() === tomorrow.toDateString()) {
          notifyShootingReminder(
            contract.id, 
            contract.client.name, 
            contract.shootingDate
          );
        }
      }
    });
  };

  // Effet pour nettoyer les notifications expirées
  useEffect(() => {
    const interval = setInterval(() => {
      cleanExpiredNotifications();
    }, 5 * 60 * 1000); // Toutes les 5 minutes

    return () => clearInterval(interval);
  }, [cleanExpiredNotifications]);

  // Effet pour vérifier les notifications au chargement
  useEffect(() => {
    if (user && contracts.length > 0) {
      checkOverduePosts();
      checkUpcomingShootings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, contracts, posts]);

  // Effet pour logger le changement d'utilisateur
  useEffect(() => {
    if (user) {
      console.log(`👤 Utilisateur actif: ${user.email}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Désactivé: Créer des notifications de test au premier chargement
  // Utilisez le bouton "Test Notifs" dans le header pour générer des notifications de test
  // useEffect(() => {
  //   if (user) {
  //     // Attendre un peu pour que les stores se chargent
  //     const timer = setTimeout(() => {
  //       createSampleNotifications();
  //     }, 2000);

  //     return () => clearTimeout(timer);
  //   }
  // }, [user]);

  return {
    createSampleNotifications,
    checkOverduePosts,
    checkUpcomingShootings
  };
};